import { useEffect } from 'react';
import { useLegalSettings, orPlaceholder } from '../hooks/useLegalSettings';
import { useLang } from '../contexts/LanguageContext';

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { settings, loading } = useLegalSettings();
  const { t } = useLang();
  const ph = t('privacy.placeholder');

  const s = settings || {};
  const updateLabel = s.updatedAtLabel || '—';
  const email = s.email || 'contact@pernetyparis.com';

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />
        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-12 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-10 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-12 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(36px, 5vw, 60px)'
          }}>
            {t('privacy.heroTitle')}
          </h1>
        </div>
      </section>

      {/* ── CONTENU ── */}
      <section className="bg-white py-20 md:py-24 px-6">
        <div className="max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>

          <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-12 text-center">
            {t('privacy.lastUpdate')} : {updateLabel}
          </p>

          {loading ? (
            <p className="text-center text-gray-400">{t('common.loading')}</p>
          ) : (
          <>

          <Block title={t('privacy.preambleTitle')}>
            <p>
              {t('privacy.preambleP1')} <strong>pernetyparis.com</strong>{t('privacy.preambleP1End')}
            </p>
            <p>{t('privacy.preambleP2')}</p>
          </Block>

          <Block title={t('privacy.s1Title')}>
            <p>{t('privacy.s1Intro')}</p>
            <ul className="space-y-1 my-4">
              <li><strong>{t('privacy.s1Identity')} :</strong> {orPlaceholder(s.legalName, 'Prénom NOM', ph)} / Pernety</li>
              <li><strong>{t('privacy.s1Status')} :</strong> {orPlaceholder(s.legalStatus, 'ex. Entrepreneur individuel', ph)}</li>
              <li><strong>{t('privacy.s1Address')} :</strong> {orPlaceholder(s.address, 'adresse complète', ph)}</li>
              <li><strong>{t('privacy.s1Siret')} :</strong> {orPlaceholder(s.siret, '14 chiffres', ph)}</li>
              <li><strong>{t('privacy.s1Email')} :</strong> {email}</li>
              <li><strong>{t('privacy.s1Phone')} :</strong> {orPlaceholder(s.phone, 'numéro de téléphone', ph)}</li>
            </ul>
            <p>{t('privacy.s1NoDpo')}</p>
          </Block>

          <Block title={t('privacy.s2Title')}>
            <p>{t('privacy.s2Intro')}</p>
            <h3 className="font-medium text-gray-800 mt-6 mb-2">{t('privacy.s2_1Title')}</h3>
            <ul className="space-y-1 my-2">
              <li>{t('privacy.s2_1L1')}</li>
              <li>{t('privacy.s2_1L2')}</li>
              <li>{t('privacy.s2_1L3')}</li>
              <li>{t('privacy.s2_1L4')}</li>
              <li>{t('privacy.s2_1L5')}</li>
              <li>{t('privacy.s2_1L6')}</li>
            </ul>
            <h3 className="font-medium text-gray-800 mt-6 mb-2">{t('privacy.s2_2Title')}</h3>
            <ul className="space-y-1 my-2">
              <li>{t('privacy.s2_2L1')}</li>
              <li>{t('privacy.s2_2L2')}</li>
              <li>{t('privacy.s2_2L3')}</li>
            </ul>
            <p className="mt-4">{t('privacy.s2NoSensitive')}</p>
          </Block>

          <Block title={t('privacy.s3Title')}>
            <p>{t('privacy.s3Intro')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li>{t('privacy.s3L1')}</li>
              <li>{t('privacy.s3L2')}</li>
              <li>{t('privacy.s3L3')}</li>
              <li>{t('privacy.s3L4')}</li>
            </ul>
            <p>{t('privacy.s3End')}</p>
          </Block>

          <Block title={t('privacy.s4Title')}>
            <p>{t('privacy.s4Intro')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>{t('privacy.s4L1Strong')}</strong>{t('privacy.s4L1')}</li>
              <li><strong>{t('privacy.s4L2Strong')}</strong>{t('privacy.s4L2')}</li>
              <li><strong>{t('privacy.s4L3Strong')}</strong>{t('privacy.s4L3')}</li>
              <li><strong>{t('privacy.s4L4Strong')}</strong>{t('privacy.s4L4')}</li>
            </ul>
          </Block>

          <Block title={t('privacy.s5Title')}>
            <p>{t('privacy.s5Intro')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>{t('privacy.s5L1Strong')}</strong>{t('privacy.s5L1')}</li>
              <li><strong>{t('privacy.s5L2Strong')}</strong>{t('privacy.s5L2')}</li>
              <li><strong>{t('privacy.s5L3Strong')}</strong>{t('privacy.s5L3')}</li>
            </ul>
            <p>{t('privacy.s5End')}</p>
          </Block>

          <Block title={t('privacy.s6Title')}>
            <p>{t('privacy.s6P1')}</p>
            <p className="mt-3">{t('privacy.s6P2')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>{t('privacy.s6L1Strong')}</strong>{t('privacy.s6L1')}
                <ul className="space-y-1 my-2 list-[circle] pl-5">
                  <li><strong>{t('privacy.s6OvhStrong')}</strong>{t('privacy.s6Ovh')}</li>
                  <li><strong>{t('privacy.s6MongoStrong')}</strong>{t('privacy.s6Mongo')}{orPlaceholder(s.mongoRegion, 'région d\'hébergement : UE/Irlande, ou hors UE', ph)}</li>
                </ul>
              </li>
              <li><strong>{t('privacy.s6Auth')}</strong>{t('privacy.s6AuthEnd')}</li>
            </ul>
          </Block>

          <Block title={t('privacy.s7Title')}>
            <p>
              {t('privacy.s7P1')} <strong>{t('privacy.s7P1Strong')}</strong>{t('privacy.s7P1End')}
            </p>
            <p>
              {t('privacy.s7P2')} <strong>{t('privacy.s7P2Strong')}</strong>{t('privacy.s7P2End')}
            </p>
          </Block>

          <Block title={t('privacy.s8Title')}>
            <p>{t('privacy.s8Intro')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>{t('privacy.s8L1Strong')}</strong>{t('privacy.s8L1')}</li>
              <li><strong>{t('privacy.s8L2Strong')}</strong>{t('privacy.s8L2')}</li>
              <li><strong>{t('privacy.s8L3Strong')}</strong>{t('privacy.s8L3')}</li>
              <li><strong>{t('privacy.s8L4Strong')}</strong>{t('privacy.s8L4')}</li>
              <li><strong>{t('privacy.s8L5Strong')}</strong>{t('privacy.s8L5')}</li>
              <li><strong>{t('privacy.s8L6Strong')}</strong>{t('privacy.s8L6')}</li>
              <li><strong>{t('privacy.s8L7Strong')}</strong>{t('privacy.s8L7')}</li>
              <li><strong>{t('privacy.s8L8Strong')}</strong>{t('privacy.s8L8')}</li>
            </ul>
            <p>{t('privacy.s8End')(email)}</p>
          </Block>

          <Block title={t('privacy.s9Title')}>
            <p>{t('privacy.s9P')}</p>
            <ul className="space-y-1 my-4">
              <li><strong>{t('privacy.s9Address')} :</strong> 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07</li>
              <li><strong>{t('privacy.s9Phone')} :</strong> +33 (0)1 53 73 22 22</li>
              <li><strong>{t('privacy.s9Web')} :</strong> <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">www.cnil.fr</a></li>
            </ul>
          </Block>

          <Block title={t('privacy.s10Title')}>
            <p>{t('privacy.s10Intro')}</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li>{t('privacy.s10L1')}</li>
              <li>{t('privacy.s10L2')}</li>
              <li>{t('privacy.s10L3')}</li>
              <li>{t('privacy.s10L4')}</li>
              <li>{t('privacy.s10L5')}</li>
            </ul>
            <p>{t('privacy.s10End')}</p>
          </Block>

          <Block title={t('privacy.s11Title')}>
            <p>{t('privacy.s11P')}</p>
          </Block>

          <Block title={t('privacy.s12Title')}>
            <p>
              {t('privacy.s12P1')(email)}
            </p>
            <p>
              {t('privacy.s12P2')} <a href="/mentions-legales" className="underline hover:text-black">{t('privacy.s12LegalLink')}</a>
            </p>
          </Block>

          </>
          )}

        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="font-light mb-5 text-gray-900" style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(20px, 1.8vw, 26px)'
      }}>
        {title}
      </h2>
      <div className="space-y-4 text-[14.5px] leading-[1.85]" style={{ color: '#4a4a4a' }}>
        {children}
      </div>
    </div>
  );
}
